import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SEOHead from '../SEOHead';
import './AdminLogin.css';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }

    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Invalid username or password. Please verify your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-login-wrapper">
      <SEOHead title="Doctor & Staff Portal Login" noindex={true} />

      <div className="login-card-container">
        {/* Brand Header */}
        <div className="login-header text-center">
          <div className="login-logo-bubble mb-3">
            <img
              src="/images/logo_new.png"
              alt="Dr. Sunil Apsingi Logo"
              className="login-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h2 className="login-title">Dr. Sunil Apsingi</h2>
          <p className="login-subtitle">Chief Arthroscopy & Joint Replacement</p>
          <div className="portal-badge">
            <i className="fa-solid fa-shield-halved me-1"></i> Clinical Operations Desk
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form mt-4">
          {error && (
            <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2 small">
              <i className="fa-solid fa-circle-exclamation text-danger fs-6"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small fw-semibold text-secondary">
              Doctor / Staff Username
            </label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fa-solid fa-user text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Enter your staff username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label small fw-semibold text-secondary">
              Security Password
            </label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fa-solid fa-lock text-muted"></i>
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-control border-start-0 border-end-0 ps-0"
                placeholder="Enter account password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="input-group-text bg-light border-start-0 cursor-pointer text-muted"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-admin-primary w-100 py-2 fw-semibold"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin me-2"></i> Authenticating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-arrow-right-to-bracket me-2"></i> Log In to Dashboard
              </>
            )}
          </button>
        </form>

        {/* Security & Return Links */}
        <div className="login-footer text-center mt-4 pt-3 border-top border-light">
          <Link to="/" className="text-decoration-none text-muted small hover-orange">
            <i className="fa-solid fa-arrow-left me-1"></i> Return to Live Patient Website
          </Link>
          <div className="mt-2 text-muted-extra-small">
            <i className="fa-solid fa-lock me-1 text-teal"></i> 256-Bit Encrypted Healthcare Session
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
